import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import axios from "axios";
import { saveAs } from "file-saver";
import { useToggle } from "hooks/useToggle";
import { useRuleValidator } from "hooks/useRuleValidator";
import { createGlobalInvoicePaymentBreakdown } from "redux/actions/globalInvoices/globalInvoicesAction";
import { PERMISSIONS } from "router/permissions";
import { useSearch } from "hooks/useSearch";
import {
  buildBreakdownModalRows,
  buildCreLicenseBreakdown,
  buildInvalidQuantityMessage,
  buildInvalidQuantityRows,
  buildOriginalPayments,
  calcPendingToBill,
  nextManualNoGn4,
  parseNoGn4FieldValue,
  paymentFieldKey,
  toggleSingleSelection,
  togglePaymentSelection,
} from "./invoiceTotals";

// Selectors
const selectToken = (state) => state.login.token;
const selectGlobalInvoice = (state) => state.globalInvoices.globalInvoice;
const selectStation = (state) => state.globalInvoices.station;
const selectStartDate = (state) => state.globalInvoices.startDate;
const selectEndDate = (state) => state.globalInvoices.endDate;
const selectLoading = (state) =>
  state.search.isLoadingComp?.loadingGlobalInvoices;

export const useFormGlobalInvoice = () => {
  const dispatch = useDispatch();
  const token = useSelector(selectToken, shallowEqual);
  const { setSnackbar } = useSearch();

  const globalInvoice = useSelector(selectGlobalInvoice, shallowEqual);
  const station = useSelector(selectStation, shallowEqual);
  const startDate = useSelector(selectStartDate, shallowEqual);
  const endDate = useSelector(selectEndDate, shallowEqual);
  const loading = useSelector(selectLoading, shallowEqual);

  const [isDownload, setIsDownload] = useState(false);
  const [openBreakdown, setOpenBreakdown, setOpenBreakdownValue] = useToggle();
  const [selectedPayments, setSelectedPayments] = useState({});
  // Solo un permiso abierto a la vez.
  const [expandedLicense, setExpandedLicense] = useState(null);
  // Facturacion No GN4 capturada a mano; null mientras se usa la del backend.
  const [manualNoGn4, setManualNoGn4] = useState(null);
  const isInvoiceValid = useRuleValidator(PERMISSIONS.INVOICES_ERP);

  const pendingByCreLicense = useMemo(
    () => globalInvoice?.pendingByCreLicense || [],
    [globalInvoice],
  );

  // Snapshot de PU y litros tal como llegaron del backend, capturado una
  // sola vez por busqueda. Vive aqui -y no en un useRef dentro del renglon
  // que edita- porque el acordeon de cada permiso se desmonta al colapsarlo
  // (Collapse unmountOnExit): un ref local se recalcularia leyendo el store
  // ya editado en vez del valor real, y "restaurar" al desmarcar Manual
  // dejaria de funcionar tras un ciclo de colapso/expansion.
  const originalPayments = useRef({});

  const billsNoGn4 = manualNoGn4 ?? globalInvoice?.billsNoGn4;

  // Al capturar No GN4 a mano, la Facturacion Pendiente refleja la resta.
  const pendingToBill = useMemo(
    () =>
      manualNoGn4
        ? calcPendingToBill(
            globalInvoice?.sales,
            globalInvoice?.billsGn4,
            manualNoGn4,
          )
        : globalInvoice?.pendingToBill,
    [manualNoGn4, globalInvoice],
  );

  const toggleManualNoGn4 = useCallback(() => {
    setManualNoGn4((prev) => nextManualNoGn4(prev, globalInvoice?.billsNoGn4));
  }, [globalInvoice]);

  const handleNoGn4Field = useCallback(
    (field) => ({ target }) => {
      const value = parseNoGn4FieldValue(target.value);
      setManualNoGn4((prev) => ({ ...(prev || {}), [field]: value }));
    },
    [],
  );

  // Ids de las facturas ya timbradas de todos los permisos de la busqueda.
  const invoiceIds = useMemo(
    () => pendingByCreLicense.flatMap((license) => license?.idsInvoices || []),
    [pendingByCreLicense],
  );

  // Los errores se avisan con snackbar y no se propagan.
  const handleDownload = useCallback(
    async (ids = []) => {
      const downloadUrl = `${process.env.REACT_APP_URL_PAY}/invoices/zip-download?qid=${process.env.REACT_APP_QID_INVOICES}`;
      const options = {
        method: "POST",
        url: downloadUrl,
        headers: { Authorization: token },
        data: { xml: true, pdf: true, ids: Array.isArray(ids) ? ids : [ids] },
        responseType: "blob",
      };
      await axios
        .request(options)
        .then((response) => {
          saveAs(
            response.data,
            `${station?.name}-${startDate}-${endDate}-invoice.zip`,
          );
        })
        .catch((error) => {
          setSnackbar(error);
        });
    },
    [token, station, startDate, endDate, setSnackbar],
  );

  const handleDownloadZip = useCallback(() => {
    setIsDownload(true);
    handleDownload(invoiceIds);
  }, [handleDownload, invoiceIds]);

  const toggleLicense = useCallback(
    (creLicense) => () =>
      setExpandedLicense((prev) => toggleSingleSelection(prev, creLicense)),
    [],
  );

  const onTogglePayment = useCallback((creLicense, taxRate, paymentKey) => {
    const key = paymentFieldKey(creLicense, taxRate, paymentKey);
    setSelectedPayments((prev) => togglePaymentSelection(prev, key));
  }, []);

  /**
   * Filas del modal de confirmacion: una por permiso y metodo de pago, con la
   * columna Permiso y el tipo automatica/manual.
   */
  const getBreakdownModalData = useCallback(
    () => buildBreakdownModalRows(pendingByCreLicense, selectedPayments),
    [selectedPayments, pendingByCreLicense],
  );

  // El SAT no acepta partidas por debajo de 1 litro (CRM-3022).
  const invalidQuantityRows = useMemo(
    () => buildInvalidQuantityRows(pendingByCreLicense),
    [pendingByCreLicense],
  );

  /**
   * El timbrado viaja con todos los permisos de la busqueda en una sola
   * operacion, y la fecha de emision la elige el usuario en el segundo modal.
   */
  const handleSubmitBreakdown = useCallback(
    async (issueDate) => {
      if (invalidQuantityRows.length > 0) {
        return setSnackbar(buildInvalidQuantityMessage(invalidQuantityRows));
      }

      const creLicenseBreakdown = buildCreLicenseBreakdown(
        pendingByCreLicense,
        selectedPayments,
      );

      if (creLicenseBreakdown.length === 0) {
        return setSnackbar("No hay valores para timbrar");
      }

      const form = {
        station: station?.id,
        date: issueDate,
        start: startDate,
        end: endDate,
        creLicenseBreakdown,
        period: {
          month: new Date(startDate).getUTCMonth() + 1,
          year: new Date(startDate).getUTCFullYear(),
        },
      };

      // El POST solo encola (CRM-3109): la respuesta es {message}, sin
      // resultado por renglon. El snackbar con ese mensaje y el cierre del
      // modal ya los maneja la propia accion; el avance real se sigue en
      // "Estatus Facturacion Global".
      await dispatch(
        createGlobalInvoicePaymentBreakdown(
          form,
          // Solo se cierra el modal cuando la peticion sale bien: si falla
          // (red, validacion) el usuario se queda ahi para reintentar.
          () => setOpenBreakdownValue(false),
        ),
      );
    },
    [
      pendingByCreLicense,
      selectedPayments,
      station,
      startDate,
      endDate,
      invalidQuantityRows,
      setSnackbar,
    ],
  );

  // Una busqueda nueva descarta las casillas Manual, cierra el acordeon y
  // vuelve a congelar los valores originales de esta respuesta.
  useEffect(() => {
    setSelectedPayments({});
    setExpandedLicense(null);
    setManualNoGn4(null);
    originalPayments.current = buildOriginalPayments(
      globalInvoice?.pendingByCreLicense || [],
    );
  }, [globalInvoice?.fetchedAt]);

  useEffect(() => {
    if (!isDownload) return undefined;
    const timeout = setTimeout(() => setIsDownload(false), 4000);
    return () => clearTimeout(timeout);
  }, [isDownload]);

  return {
    loading,
    sales: globalInvoice?.sales,
    billsGn4: globalInvoice?.billsGn4,
    billsNoGn4,
    pendingToBill,
    manualNoGn4: !!manualNoGn4,
    toggleManualNoGn4,
    handleNoGn4Field,
    totalToBill: globalInvoice?.totalToBill,
    pendingByCreLicense,
    originalPayments: originalPayments.current,
    globalInvoice,
    station,
    isInvoiceValid,
    expandedLicense,
    toggleLicense,
    invoiceIds,
    handleDownloadZip,
    isDownload,
    selectedPayments,
    onTogglePayment,
    openBreakdown,
    setOpenBreakdown,
    handleSubmitBreakdown,
    getBreakdownModalData,
    invalidQuantityRows,
  };
};
